import * as React from "react";
import { connect } from "react-redux";
import { uniq, flatten, values, debounce } from "lodash";

import { Container, Segment, Header, Loader } from "semantic-ui-react";

import { AnsweredQuestionWithId, QuestionnairesState, QuestionnaireState, QuestionnaireTypes } from "@teamprevent/common/questionnaires/model";
import { SavingIndicator } from "@teamprevent/common/components/SavingIndicator";
import { questions as healthSurveillanceQuestionnaireQuestions } from "@teamprevent/common/healthSurveillance/questions";
import { OldProgressBarQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireContainer";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import * as questionIds from "@teamprevent/common/healthSurveillance/questions/questionIds";
import { toast } from "@teamprevent/common/toasts";
import { WorkGroupDetailModel } from "@teamprevent/common/workGroup/model";

import { loadQuestionnaire, saveQuestionnaire, submitQuestionnaire } from "../actions";
import { questions as healthSurveillanceConsultationQuestions } from "../../healthSurveillance/consultationQuestions";
import { customerApi } from "../../crud/customer/customerApi";
import { orgStructureApi } from "../../crud/orgStructure/orgStructureApi";
import { workGroupApi } from "../../crud/workGroup/workGroupApi";
import { FastTrackPitPupMedicalFormQuestionnaire } from "../models";

import { ConfidentialityAndDataProtectionSection } from "./ConfidentialityAndDataProtectionSection";
import { AssociatePersonalDetailsSection } from "./AssociatePersonalDetailsSection";
import { AssociateMedicalDetailsSection } from "./AssociateMedicalDetailsSection";
import { TechnicianNotesSection } from "./TechnicianNotesSection";

import { Location } from "redux-little-router";

export const QUESTIONNAIRE_KEY = "FastTrackPitPupMedicalForm";
export const sectionId = "PersonalDetails";

export interface StateProps {
    questionnaire: QuestionnaireState;
}

export interface DispatchProps {
    loadQuestionnaire: () => Promise<FastTrackPitPupMedicalFormQuestionnaire>;
    submitQuestionnaire: (questions: AnsweredQuestionWithId[]) => Promise<string>;
    saveQuestionnaire: (questions: AnsweredQuestionWithId[], eTag: string) => Promise<void>;
}

export interface OwnProps {
    readonly?: boolean;
    questionnaire?: QuestionnaireState;
    ohCaseId?: string;
}

export type FormProps = OwnProps & DispatchProps;

export const FastTrackPitPupMedicalFormUnconnected: React.FunctionComponent<FormProps> = (props: FormProps) => {
    const [submitting, setSubmitting] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [loadComplete, setLoadComplete] = React.useState(false);
    const [eTag, setETag] = React.useState<string>();
    const [selectedCustomerId, setSelectedCustomerId] = React.useState("");
    const [fileUploadUrl, setFileUploadUrl] = React.useState("");
    const [workGroups, setWorkGroups] = React.useState<WorkGroupDetailModel[]>([]);

    React.useEffect(() => {
        (async () => {
            const savedData = await props.loadQuestionnaire();
            if (savedData) {
                setETag(savedData.eTag);
            }
            setLoadComplete(true);
        })();
    }, []);

    const save = async (questionsToSave?: AnsweredQuestionWithId[]) => {
        if (props.readonly) {
            return;
        }

        setSaving(true);
        try {
            await props.saveQuestionnaire(questionsToSave, props.questionnaire?.eTag ?? eTag);
        } finally {
            setSaving(false);
        }
    };

    const saveDebounced = debounce(async (
        questionsToSave?: AnsweredQuestionWithId[]) => {
        if (!props.saveQuestionnaire || saving || submitting) {
            return;
        }

        await save(questionsToSave);
    }, 1500);

    React.useEffect(() => {
        setFileUploadUrl(
            selectedCustomerId ?
                `/api/healthSurveillanceConsultationForCustomer/${selectedCustomerId}/evidence` :
                null);
    }, [selectedCustomerId]);

    const questions = React.useMemo(() =>
        props.questionnaire ?
            uniq(flatten(props.questionnaire.sections.map(section => values(section.questions)))) :
            [],
    [props.questionnaire]);

    React.useEffect(() => {
        if (props.readonly || !questions || !loadComplete) {
            return undefined;
        }
        saveDebounced(questions);
        return saveDebounced.cancel;
    }, [props.questionnaire?.sections]);

    const onSubmit = async (questionsToSubmit: AnsweredQuestionWithId[]): Promise<boolean> => {
        if (props.readonly) {
            return false;
        }

        saveDebounced.cancel();
        save(questionsToSubmit);

        const workGroupIdsAsString = getAnswerToQuestionWithId(questions, questionIds.WorkGroupId)?.toString();
        const workGroupIds = workGroupIdsAsString.split(",");

        const anyFastTrackWorkGroup = workGroups.some(w => workGroupIds.some(i => i === w.id) && w.isFastTrackPitPupSpecific);

        if (!anyFastTrackWorkGroup) {
            toast.warning("At-least one Fast Track Work Group should be selected");
            return false;
        }

        setSubmitting(true);

        try {
            const result = await props.submitQuestionnaire(questionsToSubmit);
            return !!result;
        } finally {
            setSubmitting(false);
        }
    };

    const onCustomerSelect = (customerId: string, workGroupOptions: WorkGroupDetailModel[]) => {
        setSelectedCustomerId(customerId);
        setWorkGroups(workGroupOptions);

    };
    const getCustomers = async () => (await customerApi.getAll()).map(o => ({
        id: o.id,
        name: o.name ?? ""
    }));
    const getOrgLevels = async (customerId: string) => (await orgStructureApi.getAll(customerId, null)).map(o => ({
        id: o.id,
        name: o.name ?? ""
    }));
    const getWorkGroups = async (customerId: string) => workGroupApi.getAll(customerId);
    const getSubOrgLevels = async (customerId: string, parentOrganisationId: string) =>
        (await orgStructureApi.getAll(customerId, parentOrganisationId)).map(o => ({
            id: o.id,
            name: o.name ?? ""
        }));

    return (
        <Container>
            <Container>
                <Segment basic>
                    <Header as="h1">Fast Track PIT/PUP Medical Form</Header>
                </Segment>
            </Container>
            <SavingIndicator display={saving} />
            {loadComplete ?
                <OldProgressBarQuestionnaireContainer
                    readonly={props.readonly}
                    loadQuestionnaire={(): void => null}
                    submitQuestionnaire={onSubmit}
                    questionnaireKey={QUESTIONNAIRE_KEY}
                    questionnaireType={QuestionnaireTypes.Other}
                >
                    <ConfidentialityAndDataProtectionSection questionConfigs={healthSurveillanceQuestionnaireQuestions} />
                    <AssociatePersonalDetailsSection
                        questionConfigs={healthSurveillanceQuestionnaireQuestions}
                        getCustomers={getCustomers}
                        getOrgLevels={getOrgLevels}
                        getWorkGroups={getWorkGroups}
                        getSubOrgLevels={getSubOrgLevels}
                        onCustomerSelect={onCustomerSelect}
                    />
                    <AssociateMedicalDetailsSection questionConfigs={healthSurveillanceQuestionnaireQuestions} />
                    <TechnicianNotesSection
                        questionConfigs={healthSurveillanceConsultationQuestions}
                        fileUploadApi={fileUploadUrl}
                    />
                </OldProgressBarQuestionnaireContainer> :
                <Loader inline active />
            }
        </Container>
    );
};

const mapStateToProps = (
    state: {
        questionnaires: QuestionnairesState;
        router: Location;
    }): StateProps => {

    const questionnaire = state.questionnaires[QUESTIONNAIRE_KEY];
    return {
        questionnaire
    };
};

const mapDispatchToProps = (dispatch: any, ownProps: OwnProps): DispatchProps => ({
    loadQuestionnaire: () =>
        dispatch(loadQuestionnaire(ownProps.ohCaseId)),
    submitQuestionnaire: (questions: AnsweredQuestionWithId[]) =>
        dispatch(submitQuestionnaire(
            questions,
            getAnswerToQuestionWithId(questions, questionIds.CustomerId)?.toString())),
    saveQuestionnaire: (questions: AnsweredQuestionWithId[], eTag: string) =>
        dispatch(saveQuestionnaire(questions, eTag))
});

const mergeProps = (stateProps: StateProps, dispatchProps: DispatchProps, ownProps: OwnProps): FormProps => {
    return {
        ...stateProps,
        loadQuestionnaire: () => dispatchProps.loadQuestionnaire(),
        submitQuestionnaire: (questions: AnsweredQuestionWithId[]) => dispatchProps.submitQuestionnaire(questions),
        saveQuestionnaire: (questions: AnsweredQuestionWithId[], eTag: string) => dispatchProps.saveQuestionnaire(questions, eTag),
        readonly: ownProps.readonly
    };
};

export const FastTrackPitPupMedicalForm = connect(mapStateToProps, mapDispatchToProps, mergeProps)(FastTrackPitPupMedicalFormUnconnected);
